import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Monta uma caixa de N unidades (metafield custom.unidades_por_caixa) com os
 * sabores do custom.pdp_sabores. No carrinho entram os proprios produtos de
 * sabor, cada um com a quantidade de unidades escolhida.
 */
class TpComboEscada extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.list = this.querySelector('[data-flavor-list]');
    this.button = this.querySelector('[data-add-to-cart]');
    this.message = this.querySelector('[data-message]');

    this.unitsPerBox = Number(this.dataset.unitsPerBox) || 1;
    this.itemUnits = Math.max(1, Number(this.dataset.itemUnits) || 1);
    this.unitPrice = (Number(this.dataset.price) || 0) / this.itemUnits;
    this.variablePrice = this.dataset.variablePrice === 'true' || this.unitPrice <= 0;
    this.tiers = [1, 2, 3].map((boxes) => ({
      boxes,
      units: boxes * this.unitsPerBox,
      off: Math.max(0, Number(this.dataset[`tier${boxes}Off`]) || 0),
    }));
    this.step = this.dataset.step === 'caixa' ? this.unitsPerBox : Math.max(1, Number(this.dataset.step) || 1);
    this.boxMode = this.step === this.unitsPerBox;
    this.flavors = this.readFlavors();

    /** @type {Map<number, number>} unidades por sabor */
    this.quantities = new Map();

    /** @type {Map<number, number>} unidades da variante ja no carrinho */
    this.cartQuantities = new Map();

    this.renderList();
    this.list?.addEventListener('click', this.handleClick, { signal });
    this.button?.addEventListener('click', this.handleAddToCart, { signal });

    this.render();
    this.syncCart();

    window.addEventListener('resize', this.positionTrack, { signal });

    // Sem isso o sabor so volta a ficar disponivel quando a secao recarrega:
    // o estoque que mostramos desconta o que esta no carrinho, entao toda
    // mudanca de carrinho (inclusive remover um item) precisa ser relida.
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
  }

  handleCartUpdate = (event) => {
    // O evento do tema ja traz o carrinho novo; so buscamos /cart.js quando ele
    // nao vem, para o sabor voltar a ficar disponivel sem esperar a rede.
    // `cart:update` tambem e disparado ao adicionar, e ai o resource traz so os
    // itens adicionados: `item_count` separa o carrinho inteiro desse pedaco.
    const cart = event?.detail?.resource;

    if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number') {
      this.applyCart(cart);
      return;
    }

    this.syncCart();
  };

  /** Guarda o que o carrinho tem de cada variante e reavalia a selecao. */
  applyCart(cart) {
    this.cartQuantities = new Map();
    (cart.items || []).forEach((item) => {
      const variantId = Number(item.variant_id);
      this.cartQuantities.set(variantId, (this.cartQuantities.get(variantId) || 0) + item.quantity);
    });

    this.clampQuantities();
    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /**
   * Le o carrinho para descontar do estoque o que ja foi adicionado, senao a
   * soma das duas adicoes estoura o estoque e a Shopify corta a quantidade.
   */
  async syncCart() {
    // Adicionar ao carrinho dispara `cart:update` e tambem chama este metodo:
    // sem isso sairiam duas buscas iguais do carrinho.
    // Se algo mudou o carrinho no meio da busca, repetimos uma vez para nao
    // ficar com um retrato velho.
    if (this.cartRequest) {
      this.cartPending = true;
      return this.cartRequest;
    }

    this.cartRequest = this.fetchCart().finally(() => {
      this.cartRequest = null;
      if (this.cartPending) {
        this.cartPending = false;
        this.syncCart();
      }
    });

    return this.cartRequest;
  }

  async fetchCart() {
    try {
      const response = await fetch(window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      this.applyCart(await response.json());
    } catch {
      // Sem o carrinho ficamos so com o estoque cheio; a Shopify ainda barra o excesso.
    }
  }

  /** Unidades do sabor que ainda cabem, ja descontando o carrinho. */
  unitsPerItem(flavor) {
    return Math.max(1, Number(flavor.unitsPerItem) || 1);
  }

  stepFor(flavor) {
    const units = this.unitsPerItem(flavor);
    return Math.ceil(this.step / units) * units;
  }

  cartUnitsOf(flavor) {
    return (this.cartQuantities.get(Number(flavor.variantId)) || 0) * this.unitsPerItem(flavor);
  }

  stockLeft(flavor) {
    if (flavor.max === null || flavor.max === undefined) return Infinity;
    return Math.max(0, Number(flavor.max) * this.unitsPerItem(flavor) - this.cartUnitsOf(flavor));
  }

  /** Derruba selecoes que passaram do estoque, por exemplo apos outra adicao. */
  clampQuantities() {
    this.flavors.forEach((flavor) => {
      const limit = this.stockLeft(flavor);
      const quantity = this.quantities.get(flavor.id) || 0;
      if (quantity > limit) {
        if (limit < 1) this.quantities.delete(flavor.id);
        else this.quantities.set(flavor.id, limit);
      }
    });
  }

  flavorById(id) {
    return this.flavors.find((flavor) => flavor.id === id);
  }

  readFlavors() {
    try {
      return JSON.parse(this.querySelector('[data-flavors]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  get total() {
    let total = 0;
    this.quantities.forEach((quantity) => (total += quantity));
    return total;
  }

  get isFull() {
    return this.total >= this.unitsPerBox;
  }

  get cartUnits() {
    let units = 0;
    this.flavors.forEach((flavor) => (units += this.cartUnitsOf(flavor)));
    return units;
  }

  get required() {
    return Math.max(1, this.unitsPerBox - this.cartUnits);
  }

  get isReady() {
    return this.total >= this.required;
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }

  renderList() {
    if (!this.list) return;

    this.list.innerHTML = this.flavors
      .map((flavor) => {
        const name = this.escape(flavor.name);
        const soldOut = !flavor.available || this.stockLeft(flavor) < 1;
        const actions = !soldOut
          ? `
            <button type="button" class="tp-combo-escada__add" data-action="add">${this.escape(this.dataset.addText)}</button>
            <span class="tp-combo-escada__stepper" data-stepper hidden>
              <button type="button" class="tp-combo-escada__step" data-action="minus" aria-label="Remover ${name}">−</button>
              <span class="tp-combo-escada__step-value" data-qty>0</span>
              <button type="button" class="tp-combo-escada__step" data-action="plus" aria-label="Adicionar ${name}">+</button>
            </span>`
          : `<span class="tp-combo-escada__sold-out">${this.escape(this.dataset.soldOutText)}</span>`;

        return `
          <li class="tp-combo-escada__flavor${soldOut ? ' is-sold-out' : ''}" data-flavor="${flavor.id}">
            ${flavor.badge ? `<span class="tp-combo-escada__flavor-badge">${this.escape(flavor.badge)}</span>` : ''}
            <span class="tp-combo-escada__flavor-media">
              ${flavor.image ? `<img class="tp-combo-escada__flavor-img" src="${this.escape(flavor.image)}" alt="" width="56" height="56" loading="lazy">` : ''}
            </span>
            <span class="tp-combo-escada__flavor-name">${name}<span class="tp-combo-escada__flavor-stock" data-stock-notice hidden></span></span>
            <span class="tp-combo-escada__flavor-actions">${actions}</span>
          </li>`;
      })
      .join('');
  }

  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!target || target.disabled) return;

    const row = target.closest('[data-flavor]');
    const flavorId = Number(row?.dataset.flavor);
    if (!flavorId) return;

    const action = target.dataset.action;
    const unitsBefore = this.cartUnits + this.total;

    if (action === 'add' || action === 'plus') {
      const flavor = this.flavorById(flavorId);
      if (!flavor) return;
      const step = this.stepFor(flavor);
      const left = this.stockLeft(flavor) - (this.quantities.get(flavorId) || 0);
      if (left < step) return;
      this.quantities.set(flavorId, (this.quantities.get(flavorId) || 0) + step);
    } else if (action === 'minus') {
      const flavor = this.flavorById(flavorId);
      const next = Math.max(0, (this.quantities.get(flavorId) || 0) - (flavor ? this.stepFor(flavor) : this.step));
      if (next === 0) this.quantities.delete(flavorId);
      else this.quantities.set(flavorId, next);
    }

    this.render();

    const lastTier = this.tiers[this.tiers.length - 1].units;
    if (unitsBefore < lastTier && this.cartUnits + this.total >= lastTier) this.celebrate();

    // Mantem o foco no controle quando o botao "Adicionar" vira stepper.
    if (action === 'add') row?.querySelector('[data-action="plus"]')?.focus();
  };

  celebrate() {
    if (this.dataset.confetti !== 'true' || !this.dataset.confettiSrc) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const styles = getComputedStyle(this);
    const colors = ['--tp-cs-active', '--tp-cs-complete', '--tp-cs-notice-bg']
      .map((name) => styles.getPropertyValue(name).trim())
      .filter(Boolean)
      .concat('#ffffff');

    import(this.dataset.confettiSrc)
      .then((module) => module.fireConfetti({ colors }))
      .catch(() => {});
  }

  money(cents) {
    return formatMoney(Math.round(cents), this.dataset.moneyFormat, this.dataset.currency);
  }

  render() {
    const full = this.isFull;

    this.querySelectorAll('[data-flavor]').forEach((row) => {
      const flavorId = Number(row.getAttribute('data-flavor'));
      const flavor = this.flavorById(flavorId);
      const quantity = this.quantities.get(flavorId) || 0;
      const left = flavor ? this.stockLeft(flavor) : Infinity;
      // Indisponivel na Shopify ou sem estoque restante; as duas coisas travam
      // a linha, senao um sabor esgotado voltava a parecer clicavel.
      const noStock = left < 1 || (flavor ? !flavor.available : false);
      const atStockLimit = left - quantity < (flavor ? this.stepFor(flavor) : this.step);
      const add = row.querySelector('[data-action="add"]');
      const stepper = row.querySelector('[data-stepper]');
      const plus = row.querySelector('[data-action="plus"]');
      const qty = row.querySelector('[data-qty]');
      const notice = row.querySelector('[data-stock-notice]');

      row.classList.toggle('is-selected', quantity > 0);
      row.classList.toggle('is-sold-out', noStock);
      if (add instanceof HTMLButtonElement) {
        add.hidden = quantity > 0 || noStock;
        add.disabled = atStockLimit;
      }
      if (stepper instanceof HTMLElement) stepper.hidden = quantity < 1;
      if (plus instanceof HTMLButtonElement) plus.disabled = atStockLimit;
      if (qty) qty.textContent = String(this.boxMode ? quantity / this.unitsPerBox : quantity);

      if (notice instanceof HTMLElement) {
        const showNotice = Number.isFinite(left) && left > 0 && (atStockLimit || left <= this.unitsPerBox);
        notice.hidden = !showNotice;
        if (showNotice) {
          notice.textContent = (this.dataset.stockLimitText || 'Só restam [restam] un.').replace('[restam]', String(left));
        }
      }
    });

    const required = this.required;
    const ready = this.isReady;
    const cartUnits = this.cartUnits;

    const counter = this.querySelector('[data-counter]');
    if (counter) {
      const boxes = this.total / this.unitsPerBox;
      counter.textContent = this.boxMode
        ? `${boxes} ${boxes === 1 ? 'caixa' : 'caixas'}`
        : cartUnits >= this.unitsPerBox
          ? `${this.total} ${this.total === 1 ? 'selecionada' : 'selecionadas'}`
          : `${this.total} de ${required}`;
      counter.classList.toggle('is-complete', ready);
    }

    const cartNote = this.querySelector('[data-cart-note]');
    if (cartNote instanceof HTMLElement) {
      cartNote.hidden = cartUnits < 1;
      const template = this.boxMode
        ? this.dataset.cartBoxesText
        : cartUnits >= this.unitsPerBox
          ? this.dataset.cartCompleteText
          : this.dataset.cartMissingText;
      cartNote.textContent = this.singular(
        (template || '')
          .replace('[caixas]', this.boxesLabel(cartUnits))
          .replace('[no_carrinho]', String(cartUnits))
          .replace('[faltam]', String(this.unitsPerBox - cartUnits))
          .replace('[minimo]', String(this.unitsPerBox)),
        this.unitsPerBox - cartUnits
      );
    }

    this.renderLadder(cartUnits + this.total);
    this.renderSummary(required, cartUnits + this.total);

    if (this.button && !this.isSubmitting) {
      const available = this.flavors.some((flavor) => flavor.available && this.stockLeft(flavor) > 0);
      const missing = required - this.total;

      this.button.disabled = !available || !ready;

      if (!available) {
        this.button.textContent = this.dataset.soldOutText;
      } else if (ready) {
        this.button.textContent = this.dataset.buttonText;
      } else if (cartUnits >= this.unitsPerBox || this.boxMode) {
        this.button.textContent = this.dataset.chooseText;
      } else {
        this.button.textContent = this.singular(this.dataset.missingText.replace('[faltam]', String(missing)), missing);
      }
    }
  }

  singular(text, count) {
    if (count !== 1) return text;
    return text.replace(/\bFaltam\b/g, 'Falta').replace(/\bfaltam\b/g, 'falta').replace(/\b1 unidades\b/g, '1 unidade');
  }

  tierFor(units) {
    return [...this.tiers].reverse().find((tier) => tier.off > 0 && units >= tier.units) || null;
  }

  tierLabel(tier) {
    if (tier.boxes === 1) return this.dataset.tierBoxText || '1 caixa';
    return (this.dataset.tierBoxesText || '[n] caixas').replace('[n]', String(tier.boxes));
  }

  boxesLabel(units) {
    if (units % this.unitsPerBox !== 0) return `${units} un.`;
    return this.tierLabel({ boxes: units / this.unitsPerBox });
  }

  renderLadder(units) {
    const ladder = this.querySelector('[data-ladder]');
    if (!(ladder instanceof HTMLElement)) return;

    const first = this.tiers[0].units;
    const last = this.tiers[this.tiers.length - 1].units;
    const progress = units <= first ? 0 : units >= last ? 100 : ((units - first) / (last - first)) * 100;
    const fill = ladder.querySelector('[data-ladder-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${progress}%`;

    const hasOff = this.tiers.some((tier) => tier.off > 0);
    ladder.classList.toggle('has-off', hasOff);
    this.positionTrack();

    this.tiers.forEach((tier) => {
      const item = ladder.querySelector(`[data-tier="${tier.boxes}"]`);
      if (!(item instanceof HTMLElement)) return;

      item.classList.toggle('is-reached', units >= tier.units);
      const price = this.unitPrice * (1 - tier.off / 100);

      this.setText('[data-tier-qty]', this.tierLabel(tier), item);
      this.setText('[data-tier-units]', `${tier.units} un.`, item);
      const priceEl = item.querySelector('[data-tier-price]');
      if (priceEl instanceof HTMLElement) priceEl.hidden = this.variablePrice;
      this.setText('[data-tier-price]', !this.variablePrice ? `${this.money(price)} ${this.dataset.perUnitText || '/ un.'}` : '', item);

      const off = item.querySelector('[data-tier-off]');
      if (off instanceof HTMLElement) {
        off.hidden = tier.off < 1;
        off.textContent = (this.dataset.offText || '[off]% Off').replace('[off]', String(tier.off));
      }

      const save = item.querySelector('[data-tier-save]');
      if (save instanceof HTMLElement) {
        const base = !this.variablePrice ? this.unitPrice : this.total > 0 ? this.selectedPrice() / this.total : 0;
        save.hidden = tier.off < 1 || base <= 0;
        const saved = (base * tier.units * tier.off) / 100;
        save.textContent = (this.dataset.saveText || 'Economize [valor]').replace('[valor]', this.money(saved));
      }
    });
  }

  positionTrack = () => {
    const ladder = this.querySelector('[data-ladder]');
    const first = this.querySelector('[data-tier="1"] .tp-combo-escada__tier-dot');
    const last = this.querySelector('[data-tier="3"] .tp-combo-escada__tier-dot');
    if (!(ladder instanceof HTMLElement) || !first || !last) return;

    const box = ladder.getBoundingClientRect();
    const a = first.getBoundingClientRect();
    const z = last.getBoundingClientRect();
    ladder.style.setProperty('--track-left', `${a.left + a.width / 2 - box.left}px`);
    ladder.style.setProperty('--track-right', `${box.right - (z.left + z.width / 2)}px`);
    ladder.style.setProperty('--track-top', `${a.top + a.height / 2 - box.top}px`);
  };

  selectedPrice() {
    let selected = 0;
    this.flavors.forEach(
      (flavor) => (selected += ((this.quantities.get(flavor.id) || 0) * Number(flavor.price)) / this.unitsPerItem(flavor))
    );
    return selected;
  }

  selectedUnitPriceRange() {
    const unitPriceOf = (flavor) => Number(flavor.price) / this.unitsPerItem(flavor);
    const selected = this.flavors.filter((flavor) => (this.quantities.get(flavor.id) || 0) > 0);
    const prices = selected.map(unitPriceOf).filter((price) => price > 0);
    if (!prices.length) return { min: this.unitPrice, max: this.unitPrice };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }

  priceRangeText(min, max, suffix) {
    if (Math.round(min) === Math.round(max)) return `${this.money(min)} ${suffix}`;
    return `${this.money(min)} ${this.dataset.rangeJoinText || 'a'} ${this.money(max)} ${suffix}`;
  }

  renderSummary(required, units) {
    const perUnit = this.dataset.perUnitText || '/ un.';
    const unitPriceText = `${this.money(this.unitPrice)} ${perUnit}`;
    const tier = this.tierFor(units);
    let original;

    if (this.boxMode) {
      original = this.selectedPrice();
      const range = this.selectedUnitPriceRange();
      const perBox = this.dataset.perBoxText || '/ caixa';
      this.setText(
        '[data-summary-units]',
        this.total > 0 ? `${this.boxesLabel(this.total)} (${this.total} un.)` : this.dataset.summaryEmptyText || ''
      );
      const mixed = Math.round(range.min) !== Math.round(range.max);
      const boxText = this.priceRangeText(range.min * this.unitsPerBox, range.max * this.unitsPerBox, perBox);
      const priceLine = this.querySelector('[data-summary-unit-price]');
      if (priceLine instanceof HTMLElement) priceLine.hidden = this.total === 0;
      this.setText(
        '[data-summary-unit-price]',
        this.total === 0 ? '' : mixed ? boxText : `${boxText} · ${this.priceRangeText(range.min, range.max, perUnit)}`
      );
    } else {
      const target = Math.max(required, this.total);
      original = this.selectedPrice() + (target - this.total) * this.unitPrice;
      this.setText('[data-summary-units]', `${target} ${target === 1 ? 'unidade' : 'unidades'}`);
      this.setText('[data-summary-unit-price]', unitPriceText);
    }

    const showOff = Boolean(tier) && original > 0;
    const final = showOff ? original * (1 - tier.off / 100) : original;
    this.setText('[data-summary-total]', this.money(final));

    const badge = this.querySelector('[data-summary-badge]');
    if (badge instanceof HTMLElement) {
      badge.hidden = !showOff;
      if (showOff) badge.textContent = (this.dataset.offText || '[off]% Off').replace('[off]', String(tier.off));
    }

    const originalEl = this.querySelector('[data-summary-original]');
    if (originalEl instanceof HTMLElement) {
      originalEl.hidden = !showOff;
      if (showOff) originalEl.textContent = this.money(original);
    }
  }

  setText(selector, text, root = this) {
    const element = root.querySelector(selector);
    if (element) element.textContent = text;
  }

  handleAddToCart = async (event) => {
    event.preventDefault();
    if (!this.button || this.button.disabled || !this.isReady) return;

    const boxId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const items = this.flavors
      .filter((flavor) => this.quantities.get(flavor.id))
      .map((flavor) => ({
        id: Number(flavor.variantId),
        quantity: Math.max(1, Math.round(this.quantities.get(flavor.id) / this.unitsPerItem(flavor))),
        properties: {
          _caixa: boxId,
          _caixa_unidades: String(this.unitsPerBox),
          _unidades_por_item: String(this.unitsPerItem(flavor)),
        },
      }));

    if (!items.length) return;

    this.isSubmitting = true;
    this.setMessage('');
    this.button.disabled = true;
    this.button.textContent = this.dataset.addingText;

    try {
      const payload = { items };
      const sections = this.getCartSectionIds();
      if (sections) payload.sections = sections;

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent(this.id, data.message || data.description || '', data.description || '', data.errors || {})
        );
        throw new Error(data.description || data.message || this.dataset.errorText);
      }

      this.dispatchEvent(
        new CartAddEvent(data, String(items[0].id), {
          source: 'product-form-component',
          itemCount: this.total,
          productId: this.dataset.productId,
          sections: data.sections,
          variantId: String(items[0].id),
        })
      );

      this.openCartDrawer();
      this.quantities.clear();
      this.syncCart();
      this.button.textContent = this.dataset.addedText;
      window.setTimeout(() => this.restoreButton(), 1300);
    } catch (error) {
      this.setMessage(error instanceof Error && error.message ? error.message : this.dataset.errorText);
      this.button.textContent = this.dataset.errorText;
      window.setTimeout(() => this.restoreButton(), 1800);
    }
  };

  restoreButton() {
    this.isSubmitting = false;
    this.render();
  }

  getCartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });
    return [...sectionIds].join(',');
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (!cartDrawer) {
      const cartUrl = window.Theme?.routes?.cart_url;
      if (cartUrl) window.location.assign(cartUrl);
      return;
    }

    customElements.whenDefined('cart-drawer-component').then(() => {
      if (typeof cartDrawer.open === 'function') cartDrawer.open();
    });
  }

  setMessage(message) {
    if (this.message) this.message.textContent = message;
  }
}

if (!customElements.get('tp-combo-escada')) {
  customElements.define('tp-combo-escada', TpComboEscada);
}
